import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import {
  Card,
  CardContent,
  Button,
  Badge,
  Input,
  Avatar,
  AvatarFallback,
  EmptyState,
  Skeleton,
  ErrorState,
} from '@/components/ui'
import { Users, Search, MoreVertical, Mail } from 'lucide-react'
import { api, type AppUserInfo } from '@/api/client'

export function AppUsersPage() {
  const { orgSlug, appId } = useParams()
  const [users, setUsers] = useState<AppUserInfo[]>([])
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchUsers = async () => {
      if (!orgSlug || !appId) return

      try {
        setError(null)
        const response = await api.apps.listUsers(orgSlug, appId)
        setUsers(response.users)
        setTotal(response.total)
      } catch (err) {
        console.error('Failed to fetch app users:', err)
        setError(err instanceof Error ? err.message : 'Failed to load users')
      } finally {
        setIsLoading(false)
      }
    }

    fetchUsers()
  }, [orgSlug, appId])

  const filteredUsers = users.filter(
    (user) =>
      (user.display_name?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (user.email?.toLowerCase() || '').includes(search.toLowerCase())
  )

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input placeholder="Search users..." className="pl-9" disabled />
          </div>
          <Button disabled>
            <Mail className="mr-2 h-4 w-4" />
            Invite User
          </Button>
        </div>
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border-subtle)]">
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">User</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">Roles</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">Joined</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">Last Active</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {[1, 2, 3].map((i) => (
                  <tr key={i} className="border-b border-[var(--border-subtle)] last:border-0">
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-10 w-10 rounded-full" />
                        <div>
                          <Skeleton className="h-4 w-24 mb-1" />
                          <Skeleton className="h-3 w-32" />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4"><Skeleton className="h-5 w-20" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-20" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-16" /></td>
                    <td className="px-6 py-4 text-right"><Skeleton className="h-8 w-8 ml-auto" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="relative w-80">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
            <Input
              placeholder="Search users..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <ErrorState
          title="Failed to load users"
          description={error}
          action={
            <Button onClick={() => window.location.reload()}>
              Try Again
            </Button>
          }
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="relative w-80">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[var(--text-muted)]" />
          <Input
            placeholder="Search users..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        <Button>
          <Mail className="mr-2 h-4 w-4" />
          Invite User
        </Button>
      </div>

      {/* Users list */}
      {filteredUsers.length === 0 ? (
        <EmptyState
          icon={Users}
          title="No users found"
          description={search ? 'Try a different search term' : 'No users have authorized this app yet'}
        />
      ) : (
        <Card>
          <CardContent className="p-0">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[var(--border-subtle)]">
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">
                    Roles
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-[var(--text-secondary)]">
                    Last Active
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-[var(--text-secondary)]">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="border-b border-[var(--border-subtle)] last:border-0"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>
                            {user.display_name?.split(' ').map((n) => n[0]).join('') ||
                             user.email?.[0]?.toUpperCase() || '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div>
                          <p className="text-sm font-medium text-[var(--text-primary)]">
                            {user.display_name || 'No name'}
                          </p>
                          <p className="text-xs text-[var(--text-muted)]">
                            {user.email || user.user_id}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-wrap gap-1">
                        {user.role_slugs.map((role) => (
                          <Badge key={role} variant="default">
                            {role}
                          </Badge>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-[var(--text-secondary)]">
                      {new Date(user.created_at).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 text-sm text-[var(--text-secondary)]">
                      {user.last_active_at
                        ? new Date(user.last_active_at).toLocaleDateString()
                        : 'Never'}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Button variant="ghost" size="sm">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      {total > users.length && (
        <p className="text-sm text-[var(--text-muted)] text-center">
          Showing {users.length} of {total} users
        </p>
      )}
    </div>
  )
}
